import IExtension from '../../IExtension';
import { Naja } from 'naja';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import html2canvas from 'html2canvas';
import Workbook from "exceljs/index";
import sleep from "sleep-promise";
import contentTranslationClient, {
    TranslationReference,
    TranslationResult,
    TranslationStatus,
} from '../../../components/ContentTranslation/ContentTranslationClient';

interface HTMLImageCloneElement extends HTMLImageElement {
    dataBtnElement: HTMLButtonElement;
    dataWorkbook: Workbook;
    dataImgDiv: HTMLDivElement;
    dataImgClone: HTMLImageElement;
    dataImageNumber: number;
    dataDivOptions: any;
    dataPostTitle: string;
}

export default class ExcelExportExtension implements IExtension {
    private canvasCounter = 0;
    private readonly translations = new Map<string, Promise<TranslationResult>>();
    private static readonly SHEET_NAME_MAX_LENGTH = 31;
    private static readonly SHEETS = [
        'negative',
        'positive',
        'neutral',
    ];

    initialize (naja: Naja): void {
        $('button[data-export-to-xlsx]').click(async (e) => {
            // show  spinner
            const btnElement = <HTMLButtonElement> e.currentTarget;
            if (btnElement.innerHTML.indexOf('spinner') === -1) {
                this.addSpinner(btnElement);
            }

            // call export
            await this.exportToXls(btnElement);
        });
    }

    private addSpinner(btnELement: HTMLButtonElement): void {
        btnELement.innerHTML = '<span class="spinner-border spinner-border-sm"></span><span class="mx-1" id="xlsExportProgress">0%</span> ' + btnELement.innerHTML;
    }

    public async exportToXls (btnElement: HTMLButtonElement) {
        this.canvasCounter = 0;
        this.translations.clear();

        // Create Workbook
        const workbook = new ExcelJS.Workbook();

        // Get base div with rated img (params)
        const processImagesBaseDivParams: [HTMLButtonElement, Workbook, HTMLElement, number][] = [];
        $('div[data-component-image-rating-view]').each((i, divEl) => {
            processImagesBaseDivParams.push([btnElement, workbook, divEl, i+1]);
        });

        const imageClones: HTMLImageCloneElement[] = [];
        for (const params of processImagesBaseDivParams) {
            imageClones.push(await this.processImagesBaseDivs(params[0], params[1], params[2], params[3]));
            await sleep(1);
            this.updateProgress(0.2*imageClones.length / (processImagesBaseDivParams.length || 1));
        }

        let imagesLoaded = 0;

        imageClones.sort((a, b) => a.dataImageNumber - b.dataImageNumber);

        for (const imageClone of imageClones) {

            await sleep(1);

            imagesLoaded++;
            this.updateProgress(0.2+0.8*imagesLoaded / (processImagesBaseDivParams.length || 1));

            await this.onLoadBackgroundImage(
                imageClone.dataBtnElement,
                imageClone.dataWorkbook,
                imageClone.dataImgDiv,
                imageClone.dataImgClone,
                imageClone.dataImageNumber,
                $('div[data-component-image-rating-view]').length,
                imageClone.dataDivOptions,
                imageClone.dataPostTitle
            );
        }
    }

    private addRatingsToXlsx (worksheet, ratings): void {
        if (ratings.length > 0) {
            for (const index in ratings) {
                const row = parseInt(index) + 1;
                worksheet.getCell('A' + row).value = row + '.';
                worksheet.getCell('A' + row).alignment = { horizontal: 'left' };
                worksheet.getCell('B' + row).value = ratings[index].userName;
                worksheet.getCell('C' + row).value = ratings[index].note;
            }
        }
    }

    private addCanvasToXlsx (workbook, worksheet, canvas): void {
        // canvas to base64
        const canvasBase64 = canvas.toDataURL();

        // add heatmap to Workbook
        const workbookImage = workbook.addImage({
            base64: canvasBase64,
            extension: 'png'
        });

        // add image to sheet
        worksheet.addImage(workbookImage, {
            tl: { col: 7, row: 0 },
            ext: { width: canvas.width, height: canvas.height }
        });
    }

    private clear (container): void {
        $(container).remove();
    }

    private createImgRating (
        btnElement: HTMLButtonElement,
        workbook: Workbook,
        image,
        ratings,
        destinationContainer,
        currentImageNumber: number,
        sheetType: string,
        postTitle: string,
        importCallback
    ): void {
        $(image).imgRating({
            icon: {
                borderColor: '#23426f',
                backgroundColor: '#337ab7',
                textColor: '#23426f'
            },
            beforeIconCreated: function (iconOptions, data) {
                iconOptions.html = '<div style="color:#fff;position:absolute;left:0;padding:5px;text-align:center;width:100%;">' + data.index + '</div>';
                if (data.rating > 0) {
                    iconOptions.borderColor = '#3c763d';
                    iconOptions.backgroundColor = '#69AA46';
                    iconOptions.textColor = '#3c763d';
                } else if (data.rating < 0) {
                    iconOptions.borderColor = '#a94442';
                    iconOptions.backgroundColor = '#d74543';
                    iconOptions.textColor = '#a94442';
                }
            },
            onImport: function (data) {
                const heatmapData = [];
                for (const item of data) {
                    const loc = this.relposToLatLng(item.x, item.y);
                    heatmapData.push([loc.lat, loc.lng, item.rating]);
                }
                L.heatLayer(heatmapData, { minOpacity: 0.80, blur: 30, radius: 30 }).addTo(this.map);

                importCallback.apply(this, [btnElement, workbook, destinationContainer, currentImageNumber, sheetType, postTitle]);
            },
            onMarkerAdded: function (marker, data) {
                const rrosePopup = new L.Rrose({ offset: new L.Point(0, -10), closeButton: false, autoPan: false });
                rrosePopup.setContent(data.index);

                marker.on("add", function (event) {
                    event.target.openPopup();
                });

                marker.bindPopup(rrosePopup);
            },
            viewDestination: destinationContainer
        });
        $(image).imgRating('clear');
        $(image).imgRating('import', ratings);
    }

    private async createWorksheet (btnElement: HTMLButtonElement, workbook: Workbook, heatmapContainer: HTMLElement, ratings: any, imgDiv: HTMLDivElement, currentImageNumber: number, sheetsMaxCount: number, sheetTypeName: string, postTitle: string) {
        // Create worksheet title
        const worksheetTitle = this.createWorksheetTitle(currentImageNumber, sheetsMaxCount, sheetTypeName, postTitle);

        // check worksheet
        if (workbook.getWorksheet(worksheetTitle)) {
            return;
        }

        // Create worksheet
        const worksheet = workbook.addWorksheet(worksheetTitle);

        // add rating to xlsx
        this.addRatingsToXlsx(worksheet, ratings);

        // convert heatmap to canvas
        const canvas = await html2canvas(heatmapContainer);

        this.canvasCounter++;

        canvas.getContext("2d", {
            willReadFrequently: true,
        });

        // add canvas to xlsx
        this.addCanvasToXlsx(workbook, worksheet, canvas);

        // check heatmaps count
        if (this.canvasCounter === (sheetsMaxCount * 3)) {

            // Uložení sešitu
            this.writeXlsx(workbook, imgDiv);

            // Remove button spinner
            this.removeSpinner(btnElement);

            this.canvasCounter = 0;
        }

    }

    private createWorksheetTitle (currentImageNumber: number, sheetsMaxCount: number, sheetTypeName: string, postTitle: string): string {
        let preAndPostTitleLength = (String((sheetsMaxCount > 1 ? currentImageNumber + '. ' : '') + ' ' + sheetTypeName)).trim().length + 1;
        let sheetTitleBase = postTitle;
        if (postTitle.length + preAndPostTitleLength > ExcelExportExtension.SHEET_NAME_MAX_LENGTH) {
            sheetTitleBase = postTitle.substring(0, ExcelExportExtension.SHEET_NAME_MAX_LENGTH - preAndPostTitleLength).trim();
        }

        return (sheetTypeName + ' ' + (sheetsMaxCount > 1 ? currentImageNumber + '. ' : '') + sheetTitleBase).trim();
    }

    private getRatings (ratingsToFilter, ratingType) {
        return ratingsToFilter.filter(function (rating) {
            if (ratingType === 'negative') {
                return rating.rating < 0;
            } else if (ratingType === 'positive') {
                return rating.rating > 0;
            } else if (ratingType === 'neutral') {
                return rating.rating === 0;
            }
        });
    }

    private async onLoadBackgroundImage (
        btnElement: HTMLButtonElement,
        workbook: Workbook,
        imgDiv: HTMLDivElement,
        imgClone: HTMLElement,
        currentImageNumber: number,
        sheetsMaxCount: number,
        divOptions: any,
        postTitle: string
    ) {
        const that = this;

        // Prochazeni sheetu
        for (const sheetType in ExcelExportExtension.SHEETS) {
            const sheetTypeName = ExcelExportExtension.SHEETS[sheetType];
            // get ratings by sheet
            const ratings = that.getRatings(divOptions.ratings, sheetTypeName);

            // sort ratings points
            ratings.sort(function (a, b) {
                if (a.y < b.y) {
                    return -1;
                } else if (b.y < a.y) {
                    return 1;
                } else {
                    return 0;
                }
            });

            // add index info
            for (const i in ratings) {
                ratings[i].index = parseInt(i) + 1;
            }

            // Create heatmap on image
            that.createImgRating(
                btnElement,
                workbook,
                imgClone,
                ratings,
                imgDiv,
                currentImageNumber,
                sheetTypeName,
                postTitle,
                async function (
                    btnElement: HTMLButtonElement,
                    workbook: Workbook,
                    imgDivContainer: HTMLDivElement,
                    imageNumber: number,
                    sheetTypeName: string,
                    postTitle: string
                ) {
                    // Get final container
                    const heatmapContainer = this.map.getContainer();

                    await that.createWorksheet(btnElement, workbook, heatmapContainer, ratings, imgDivContainer, imageNumber, sheetsMaxCount, sheetTypeName, postTitle);
                }
            );
        }
    }

    private async processImagesBaseDivs (btnElement: HTMLButtonElement, workbook: Workbook, baseDiv: HTMLElement, currentImageNumber: number): Promise<HTMLImageCloneElement> {
        // Get options
        const divOptions = $(baseDiv).data('options');

        let postTitle = $(baseDiv).data('title');
        const title = await this.translate({type: 'discussion', id: String($(baseDiv).data('post-id'))}, true);
        if (title.status === TranslationStatus.Translated) {
            postTitle = title.fields?.title ?? postTitle;
        }
        postTitle = postTitle.replace(/[&\/\\#,+()$~%.'":*?<>{}\[\]]/g, ' ');

        const ratings = [];
        for (let offset = 0; offset < divOptions.ratings.length; offset += 20) {
            const batch = await Promise.all(divOptions.ratings.slice(offset, offset + 20).map(async rating => {
                const copy = {...rating};
                if (copy.note.trim() !== '') {
                    const result = await this.translate({type: 'image_rating', id: String(copy.ratingId)});
                    if (result.status === TranslationStatus.Translated) {
                        copy.note = result.fields?.note ?? copy.note;
                    }
                }
                return copy;
            }));
            ratings.push(...batch);
        }

        // Get base rated image
        const $jQueryBckImg = $(baseDiv).find('img[data-image]');

        return this.processRatedImage(btnElement, workbook, <HTMLImageElement> $jQueryBckImg[0], {...divOptions, ratings}, postTitle, currentImageNumber);
    }

    private translate(reference: TranslationReference, individual = false): Promise<TranslationResult> {
        const key = JSON.stringify([reference.type, reference.id]);
        if (!this.translations.has(key)) {
            this.translations.set(key, contentTranslationClient.request(reference, individual));
        }
        return this.translations.get(key);
    }

    private async processRatedImage (btnElement: HTMLButtonElement, workbook: Workbook, ratedImage: HTMLImageElement, divOptions: any, postTitle: any, currentImageNumber: number): Promise<HTMLImageCloneElement> {
        // Copy heatmap image (we don't want to create heatmap on the original img element - it'll be rewrote ;-) & we can hide this container)
        const imgDiv = document.createElement('div');
        imgDiv.setAttribute('class', 'heatmap-img-copy');
        imgDiv.style.position = 'absolute';
        imgDiv.style.left = '-5000px';
        imgDiv.style.top = '-5000px';
        const imgClone = <HTMLImageCloneElement> document.createElement('img');
        $(imgClone).appendTo(imgDiv);
        $(imgDiv).appendTo('body');

        // save data for local use
        imgClone.dataBtnElement = btnElement;
        imgClone.dataWorkbook = workbook;
        imgClone.dataImgDiv = imgDiv;
        imgClone.dataImgClone = imgClone;
        imgClone.dataDivOptions = divOptions;
        imgClone.dataPostTitle = postTitle;
        imgClone.dataImageNumber = currentImageNumber;


        imgClone.onload = null; // remove action - leaflet calls this event
        return new Promise(resolve=> {
            imgClone.addEventListener('load', ()=> resolve(imgClone));
            imgClone.src = ratedImage.src;
        })
    }

    private removeSpinner (btnElement): void {
        $('#xlsExportProgress').remove();
        btnElement.innerHTML = btnElement.innerText.trim();
    }

    private updateProgress(progress: number) {
        const percent = Math.max(Math.min(Math.round(progress * 100), 100), 0) + ' %';
        $('#xlsExportProgress').text(percent)
    }

    private async writeXlsx (workbook, htmlContainer) {
        try {
            const buffer = await workbook.xlsx.writeBuffer();
            // Vytvoření Blob objektu z bufferu
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            // Použití FileSaver k uložení souboru a nabídnutí ke stažení
            saveAs(blob, 'export.xlsx');

            this.clear(htmlContainer);
        } catch (error) {
            alert('Export failed.');
            console.error('Chyba při vytváření sešitu:', error);
        }
    }
}
